import { isCardResult, Question, SlideComponentCharacterCards } from '@/types/slide';
import { gql, useQuery } from '@apollo/client';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Box, Skeleton, Stack } from '@mui/material';
import { CardContainer, CardDisplay, CardDisplayProps } from '../displays/CardDisplay';

const shadowGlow = '0 0 4px 4px';

const DISPLAY_SIZE = {
    width: 200,
    height: 200,
};

type GetCharacterResponse = {
    character: {
        id: string;
        cardImg1: string;
        cardImg2: string;
        cardType: number;
        character: string;
        serial1: string[];
        serial2: string[];
        code: string;
        locale: string;
    };
};

export const GET_CHARACTER = gql`
    query Character($where: CharacterWhereUniqueInput!) {
        character(where: $where) {
            id
            cardImg1
            cardImg2
            cardType
            character
            serial1
            serial2
            code
            locale
        }
    }
`;

const NoAnswerCardDisplay = (props: CardDisplayProps) => {
    return (
        <CardDisplay sx={(theme) => ({ boxShadow: `${shadowGlow} ${(theme.vars || theme).palette.warning.light}` })} aria-label='no-answer-card' {...props}>
            <QuestionMarkIcon sx={(theme) => ({ width: 60, height: 60, color: (theme.vars || theme).palette.grey[800] })} />
        </CardDisplay>
    );
};

export function ResultDisplayCharacterCard({ question }: { question: Question & SlideComponentCharacterCards }) {
    const answer = question.answers?.[0];
    const code = question.content.code;
    const { data, loading } = useQuery<GetCharacterResponse>(GET_CHARACTER, {
        variables: { where: { code: code } },
    });
    const content = answer?.data ? JSON.parse(answer.data) : undefined;
    const result = content && isCardResult(content) ? content : undefined;
    const locale = data?.character?.locale ?? 'zh-HK';
    let serial1: string | undefined = undefined;
    let serial2: string | undefined = undefined;
    let card1Orientation = result?.card.detectedCard1.orientation;
    let card2Orientation = result?.card.detectedCard2.orientation;
    let isCard1SerialCorrect: boolean | undefined = undefined;
    let isCard2SerialCorrect: boolean | undefined = undefined;
    let isCard1OrientationCorrect: boolean | undefined = undefined;
    let isCard2OrientationCorrect: boolean | undefined = undefined;
    const correctOrientation = data?.character?.cardType === 2 ? 'Right' : 'Up';
    if (question.content.settings?.gameType === 'First') {
        serial1 = result?.card?.detectedCard1?.serial;
        serial2 = data?.character?.serial2?.[0];
        isCard1SerialCorrect = !!result?.card?.detectedCard1?.serial && !!data?.character?.serial1 && data?.character?.serial1.includes(result.card.detectedCard1.serial);

        if (data?.character?.cardType === 2) {
            // is always right when only one card type
            card2Orientation = correctOrientation;
            isCard1OrientationCorrect = !!card1Orientation && card1Orientation === correctOrientation;
        } else {
            // is always up when only one card type
            card2Orientation = correctOrientation;
            isCard1OrientationCorrect = !!card1Orientation && card1Orientation === correctOrientation;
        }
    } else if (question.content.settings?.gameType === 'Second') {
        serial1 = data?.character?.serial1?.[0];
        serial2 = result?.card?.detectedCard2?.serial;
        isCard2SerialCorrect = !!result?.card?.detectedCard2?.serial && !!data?.character?.serial2 && data?.character?.serial2.includes(result.card.detectedCard2.serial);
        if (data?.character?.cardType === 2) {
            // is always right when only one card type
            card1Orientation = correctOrientation;
            isCard2OrientationCorrect = !!card2Orientation && card2Orientation === correctOrientation;
        } else {
            // is always up when only one card type
            card1Orientation = correctOrientation;
            isCard2OrientationCorrect = !!card2Orientation && card2Orientation === correctOrientation;
        }
    } else if (question.content.settings?.gameType === 'Both') {
        serial1 = result?.card?.detectedCard1?.serial;
        serial2 = result?.card?.detectedCard2?.serial;
        isCard1SerialCorrect = !!result?.card?.detectedCard1?.serial && !!data?.character?.serial1 && data.character.serial1.includes(result.card.detectedCard1.serial);
        isCard2SerialCorrect = !!result?.card?.detectedCard2?.serial && !!data?.character?.serial2 && data.character.serial2.includes(result.card.detectedCard2.serial);
        isCard1OrientationCorrect = !!result?.card?.detectedCard1?.orientation && result.card.detectedCard1.orientation === correctOrientation;
        isCard2OrientationCorrect = !!result?.card?.detectedCard2?.orientation && result.card.detectedCard2.orientation === correctOrientation;
    }

    const img1 = `/asset/card/${locale}/${serial1}.png`;
    const img2 = `/asset/card/${locale}/${serial2}.png`;
    const direction = data?.character?.cardType === 2 ? 'column' : 'row';
    const showOrder = data?.character?.cardType === 3;

    return loading ? (
        <Skeleton variant='rectangular' width={DISPLAY_SIZE.width} height={DISPLAY_SIZE.height} aria-label='skeleton' />
    ) : (
        <Box>
            <Stack spacing={1.5} direction={direction}>
                <CardContainer>
                    {serial1 ? (
                        <CardDisplay
                            direction={direction}
                            orientation={card1Orientation}
                            imgUrl={img1}
                            order={showOrder ? 1 : undefined}
                            sx={(theme) => ({
                                boxShadow:
                                    (isCard1SerialCorrect && isCard1OrientationCorrect) === true
                                        ? `${shadowGlow} ${(theme.vars || theme).palette.success.light}`
                                        : (isCard1SerialCorrect && isCard1OrientationCorrect) === false
                                          ? `${shadowGlow} ${(theme.vars || theme).palette.error.light}`
                                          : theme.shadows[1],
                            })}
                            aria-label='card1'
                        />
                    ) : (
                        <NoAnswerCardDisplay direction={direction} />
                    )}
                </CardContainer>
                <CardContainer>
                    {serial2 ? (
                        <CardDisplay
                            direction={direction}
                            orientation={card2Orientation}
                            imgUrl={img2}
                            order={showOrder ? 2 : undefined}
                            sx={(theme) => ({
                                boxShadow:
                                    (isCard2SerialCorrect && isCard2OrientationCorrect) === true
                                        ? `${shadowGlow} ${(theme.vars || theme).palette.success.light}`
                                        : (isCard2SerialCorrect && isCard2OrientationCorrect) === false
                                          ? `${shadowGlow} ${(theme.vars || theme).palette.error.light}`
                                          : theme.shadows[1],
                            })}
                            aria-label='card2'
                        />
                    ) : (
                        <NoAnswerCardDisplay direction={direction} />
                    )}
                </CardContainer>
            </Stack>
        </Box>
    );
}
